import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BackLink } from "@/components/BackLink";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Shirt, ArrowLeft, Camera, CheckCircle2, Search, Hash } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLE = {
  fulfilled: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
  partially_fulfilled: "bg-amber-500/20 text-amber-300 border-amber-500/40",
  ordered: "bg-blue-500/20 text-blue-300 border-blue-500/40",
  size_selected: "bg-slate-500/20 text-slate-300 border-slate-500/40",
  pending_size_selection: "bg-slate-500/20 text-slate-300 border-slate-500/40",
};
const STATUS_LABEL = {
  fulfilled: "Handed over",
  partially_fulfilled: "Partial",
  ordered: "Ready",
  size_selected: "Ready",
  pending_size_selection: "Awaiting size",
};
// Plain-English meaning, shown under the badge so coaches aren't guessing.
const STATUS_HELP = {
  pending_size_selection: "The parent hasn't chosen a kit size yet — you can still hand over what's in stock and record the size you actually gave below.",
  ordered: "Ordered from the supplier and ready to hand over.",
  size_selected: "Size confirmed by the parent — ready to hand over.",
  partially_fulfilled: "Some items handed over, the rest still outstanding.",
};

function stockLeft(stock, component, size) {
  const r = (stock || []).find((x) => x.item_type === component && x.size === size);
  if (!r || !r.qty_in) return null;
  return r.available;
}

export default function CoachKitHandover() {
  const [rows, setRows] = useState([]);
  const [scope, setScope] = useState("coach");
  const [sizes, setSizes] = useState([]);
  const [stock, setStock] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [r, s] = await Promise.all([
        axios.get(`${API}/kit/coach/fulfillment-queue?search=${encodeURIComponent(search)}`, { withCredentials: true }),
        axios.get(`${API}/kit/stock`, { withCredentials: true }).catch(() => ({ data: { rows: [] } })),
      ]);
      setRows(r.data.rows || []);
      setSizes(r.data.available_sizes || []);
      setStock(s.data.rows || []);
      setScope(r.data.scope || "coach");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load kit queue");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  return (
    <div className="min-h-screen bg-slate-950 text-white p-4 sm:p-6">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Shirt className="w-6 h-6 text-fuchsia-400" />
            <h1 className="text-xl sm:text-2xl font-bold">Kit Handover</h1>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/academy/coach/kit-numbers" className="text-fuchsia-300 hover:text-fuchsia-200 flex items-center gap-1 text-sm" data-testid="kit-handover-to-numbers">
              <Hash className="w-4 h-4" /> Numbers
            </Link>
            <BackLink to="/academy/coach-dashboard" label="Back"
              className="text-slate-400 hover:text-white"
              testId="kit-handover-back" />
          </div>
        </div>
        <p className="text-sm text-slate-400 mb-4">
          Tick the items you physically hand to each player. Partial handovers are saved — finish the rest later.
          {scope === "all" && <span className="text-fuchsia-300"> (Showing all teams.)</span>}
        </p>

        <div className="flex gap-2 mb-4">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <Input className="pl-9 bg-slate-900 border-slate-700" placeholder="Search player…" value={search}
              onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} data-testid="kit-handover-search" />
          </div>
          <Button onClick={load} className="bg-fuchsia-600 hover:bg-fuchsia-500" data-testid="kit-handover-search-btn">Search</Button>
        </div>

        <StockStrip stock={stock} />

        {loading ? (
          <p className="text-slate-400" data-testid="kit-handover-loading">Loading…</p>
        ) : rows.length === 0 ? (
          <Card className="bg-slate-900/40 border-slate-700"><CardContent className="py-10 text-center text-slate-400" data-testid="kit-handover-empty">
            No kit awaiting handover right now.
          </CardContent></Card>
        ) : (
          <div className="space-y-4">
            {rows.map((row) => <HandoverCard key={row.id} row={row} sizes={sizes} stock={stock} onSaved={load} />)}
          </div>
        )}
      </div>
    </div>
  );
}

function StockStrip({ stock }) {
  const jerseys = (stock || []).filter((r) => r.item_type === "jersey" && r.qty_in);
  if (jerseys.length === 0) return null;
  return (
    <div className="mb-4 bg-slate-900/40 border border-slate-700 rounded px-3 py-2" data-testid="kit-stock-strip">
      <p className="text-xs text-slate-400 mb-1">Shirts on hand — swap guide</p>
      <div className="flex flex-wrap gap-1.5">
        {jerseys.map((r) => (
          <span
            key={r.size}
            className={`text-[11px] px-2 py-1 rounded border font-mono ${
              r.available <= 0
                ? "bg-red-500/10 border-red-500/40 text-red-300"
                : r.available <= 2
                ? "bg-amber-500/10 border-amber-500/40 text-amber-300"
                : "bg-emerald-500/10 border-emerald-500/40 text-emerald-300"
            }`}
            data-testid={`kit-stock-jersey-${r.size}`}
          >
            {r.size}: {r.available}
          </span>
        ))}
      </div>
    </div>
  );
}

function HandoverCard({ row, sizes = [], stock = [], onSaved }) {
  const [delivered, setDelivered] = useState(() => new Set(row.checklist.filter((c) => c.delivered).map((c) => c.id)));
  const [issuedSizes, setIssuedSizes] = useState(() => {
    const init = {};
    row.checklist.forEach((c) => { init[c.id] = c.issued_size || c.size || ""; });
    return init;
  });
  const [swapReason, setSwapReason] = useState("");
  const [remarks, setRemarks] = useState(row.handover_remarks || "");
  const [photo, setPhoto] = useState(null);
  const [busy, setBusy] = useState(false);
  const [kitNumber, setKitNumber] = useState(row.jersey_number ?? "");
  const [savedNumber, setSavedNumber] = useState(row.jersey_number ?? null);
  const [savingNumber, setSavingNumber] = useState(false);

  const saveKitNumber = async () => {
    const n = parseInt(kitNumber, 10);
    if (!n || n <= 0) {
      toast.error("Enter the kit number printed on the shirt");
      return;
    }
    setSavingNumber(true);
    try {
      await axios.post(
        `${API}/kit/coach/assign-jersey/${row.id}`,
        { jersey_number: n, override_existing: savedNumber != null },
        { withCredentials: true }
      );
      setSavedNumber(n);
      toast.success(`Kit #${n} recorded for ${row.player_name}`);
    } catch (e) {
      const detail = e.response?.data?.detail || "Could not save the kit number";
      toast.error(detail);
    } finally {
      setSavingNumber(false);
    }
  };

  const toggle = (id) => setDelivered((prev) => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });

  const save = async () => {
    setBusy(true);
    try {
      if (photo) {
        const fd = new FormData();
        fd.append("file", photo);
        await axios.post(`${API}/kit/coach/handover-photo/${row.id}`, fd, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } });
      }
      const r = await axios.post(`${API}/kit/coach/handover/${row.id}`, {
        delivered: Array.from(delivered),
        issued_sizes: issuedSizes,
        size_swap_reason: swapReason,
        remarks,
      }, { withCredentials: true });
      toast.success(r.data.fully_fulfilled ? "Kit fully handed over ✓" : "Partial handover saved");
      onSaved && onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save handover");
    } finally {
      setBusy(false);
    }
  };

  const allDone = row.checklist.length > 0 && row.checklist.every((c) => delivered.has(c.id));
  const anySwap = row.checklist.some((c) => {
    const g = issuedSizes[c.id];
    return !!g && !!c.size && g !== c.size;
  });

  return (
    <Card className="bg-slate-900/40 border-slate-700" data-testid={`handover-card-${row.id}`}>
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <CardTitle className="text-base flex items-center gap-2">
            {row.player_name}
            {savedNumber != null && (
              <span className="font-mono text-fuchsia-300 text-sm" data-testid={`handover-kitnumber-${row.id}`}>
                #{savedNumber}
              </span>
            )}
          </CardTitle>
          <Badge
            className={STATUS_STYLE[row.kit_status] || STATUS_STYLE.size_selected}
            title={STATUS_HELP[row.kit_status] || ""}
            data-testid={`handover-status-${row.id}`}
          >
            {STATUS_LABEL[row.kit_status] || row.kit_status}
          </Badge>
        </div>
        <p className="text-xs text-slate-400">{row.age_group || "—"} · {row.entitlement_type === "additional" ? "Extra (paid)" : "Registration kit"}</p>
        {STATUS_HELP[row.kit_status] && (
          <p className="text-xs text-amber-300/80 mt-1" data-testid={`handover-status-help-${row.id}`}>
            {STATUS_HELP[row.kit_status]}
          </p>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap items-center gap-2 bg-slate-800/40 rounded px-3 py-2">
          <span className="text-xs text-slate-400 shrink-0">Kit number issued</span>
          <Input
            type="number"
            inputMode="numeric"
            min="1"
            className="bg-slate-900 border-slate-700 h-10 w-24 text-base"
            placeholder="#"
            value={kitNumber}
            onChange={(e) => setKitNumber(e.target.value)}
            data-testid={`handover-kitnumber-input-${row.id}`}
          />
          <Button
            size="sm"
            variant="outline"
            className="h-10 border-fuchsia-500/40 text-fuchsia-200"
            onClick={saveKitNumber}
            disabled={savingNumber || String(kitNumber) === String(savedNumber ?? "")}
            data-testid={`handover-kitnumber-save-${row.id}`}
          >
            {savingNumber ? "Saving…" : savedNumber != null ? "Update" : "Save #"}
          </Button>
          {savedNumber != null && <Hash className="w-4 h-4 text-emerald-400" />}
        </div>
        <div className="space-y-2">
          {row.checklist.map((c) => {
            const given = issuedSizes[c.id] || "";
            const swapped = !!given && !!c.size && given !== c.size;
            return (
              <div key={c.id} className="bg-slate-800/40 rounded px-3 py-2 space-y-2" data-testid={`handover-item-${row.id}-${c.id}`}>
                <label className="flex items-center gap-3 text-sm cursor-pointer">
                  <Checkbox checked={delivered.has(c.id)} onCheckedChange={() => toggle(c.id)} />
                  <span className={delivered.has(c.id) ? "line-through text-slate-400" : ""}>{c.label}</span>
                  {delivered.has(c.id) && <CheckCircle2 className="w-4 h-4 text-emerald-400 ml-auto" />}
                </label>
                <div className="flex flex-wrap items-center gap-2 pl-8">
                  <span className="text-xs text-slate-400 shrink-0">Size given</span>
                  <select
                    value={given}
                    onChange={(e) => setIssuedSizes((s) => ({ ...s, [c.id]: e.target.value }))}
                    className="bg-slate-900 border border-slate-700 rounded h-9 px-2 text-sm text-white"
                    data-testid={`handover-issued-size-${row.id}-${c.id}`}
                  >
                    <option value="">— not recorded —</option>
                    {sizes.map((s) => {
                      const left = stockLeft(stock, c.component, s);
                      return (
                        <option key={s} value={s}>
                          {s}{left == null ? "" : ` (${left} left)`}
                        </option>
                      );
                    })}
                  </select>
                  {swapped && (
                    <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/40 text-[10px]" data-testid={`handover-swap-badge-${row.id}-${c.id}`}>
                      swapped from {c.size}
                    </Badge>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        {anySwap && (
          <Input className="bg-slate-800 border-amber-500/40 text-sm" placeholder="Why the different size? (e.g. U10 too tight, gave U12)"
            value={swapReason} onChange={(e) => setSwapReason(e.target.value)} data-testid={`handover-swap-reason-${row.id}`} />
        )}
        {(row.size_swap_log || []).length > 0 && (
          <p className="text-[11px] text-amber-300/90" data-testid={`handover-swap-history-${row.id}`}>
            Previously swapped: {(row.size_swap_log || []).map((s) => `${s.ordered_size} → ${s.issued_size}`).join(", ")}
          </p>
        )}
        <Input className="bg-slate-800 border-slate-700 text-sm" placeholder="Remarks (e.g. socks on back-order)"
          value={remarks} onChange={(e) => setRemarks(e.target.value)} data-testid={`handover-remarks-${row.id}`} />
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <label className="flex items-center gap-2 text-xs text-slate-400 cursor-pointer min-h-[44px]">
            <Camera className="w-4 h-4" />
            <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => setPhoto(e.target.files?.[0] || null)} data-testid={`handover-photo-${row.id}`} />
            {photo ? photo.name.slice(0, 18) : (row.has_photo ? "Replace photo" : "Add photo")}
          </label>
          <Button onClick={save} disabled={busy} className={`h-11 flex-1 sm:flex-none ${allDone ? "bg-emerald-600 hover:bg-emerald-500" : "bg-fuchsia-600 hover:bg-fuchsia-500"}`} data-testid={`handover-save-${row.id}`}>
            {busy ? "Saving…" : allDone ? "Complete handover" : "Save partial"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
